class InfiniteScrollGrid extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.sentinel = this.querySelector('.infinite-scroll-grid__sentinel');
    this.status = this.querySelector('.infinite-scroll-grid__status');
    this.paginationWrapper = this.querySelector('.infinite-scroll-grid__pagination');
    const container = this.closest('#ProductGridContainer');
    this.grid = container ? container.querySelector('.collection__grid') : null;
  }

  connectedCallback() {
    if (!this.sentinel || !this.grid || !('IntersectionObserver' in window)) return;

    // Numbered pagination stays in the DOM (for no-JS visitors, crawlers, and as a
    // fallback if fetch fails) but is visually hidden once infinite scroll is active.
    if (this.paginationWrapper) this.paginationWrapper.classList.add('sr-only');

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.loading) this.loadNextPage();
        });
      },
      { rootMargin: '600px 0px 600px 0px' }
    );
    this.observer.observe(this.sentinel);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  getNextUrl() {
    const nextLink = this.paginationWrapper && this.paginationWrapper.querySelector('.pagination__item--next');
    return nextLink ? nextLink.getAttribute('href') : null;
  }

  // Fades + slides each newly-added card in, one after another, for a light
  // "slow motion" cascade instead of the whole batch popping in at once.
  animateNewItems(items) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || items.length === 0) return;

    const staggerStepMs = 110; // gap between each card's start
    const durationMs = 700; // how long each card's own fade/slide takes
    const maxStaggeredItems = 12; // cap so a big batch doesn't take forever to fully appear

    items.forEach((item, index) => {
      const delay = Math.min(index, maxStaggeredItems) * staggerStepMs;
      item.style.opacity = '0';
      item.style.transform = 'translateY(18px)';
      item.style.transition = `opacity ${durationMs}ms ease, transform ${durationMs}ms ease`;
      item.style.transitionDelay = `${delay}ms`;
    });

    // Wait a couple of frames so the browser registers the "before" state,
    // then flip to the "after" state so the transition actually animates.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        items.forEach((item) => {
          item.style.opacity = '1';
          item.style.transform = 'translateY(0)';
        });
      });
    });

    const lastDelay = Math.min(items.length - 1, maxStaggeredItems) * staggerStepMs;
    const totalTime = lastDelay + durationMs + 80;
    setTimeout(() => {
      items.forEach((item) => {
        item.style.transition = '';
        item.style.transitionDelay = '';
        item.style.opacity = '';
        item.style.transform = '';
      });
    }, totalTime);
  }

  async loadNextPage() {
    const nextUrl = this.getNextUrl();
    if (!nextUrl) {
      this.observer.disconnect();
      return;
    }

    this.loading = true;
    if (this.status) this.status.hidden = false;

    try {
      const response = await fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newItems = Array.from(
        doc.querySelectorAll('#ProductGridContainer .collection__grid .collection__grid-item')
      );
      const fragment = document.createDocumentFragment();
      newItems.forEach((item) => fragment.appendChild(item));
      this.grid.appendChild(fragment);
      this.animateNewItems(newItems);

      const newPaginationWrapper = doc.querySelector('#ProductGridContainer .infinite-scroll-grid__pagination');
      if (this.paginationWrapper) {
        if (newPaginationWrapper) {
          this.paginationWrapper.innerHTML = newPaginationWrapper.innerHTML;
          this.paginationWrapper.classList.add('sr-only');
        } else {
          this.paginationWrapper.innerHTML = '';
        }
      }

      history.replaceState(history.state, '', nextUrl);

      if (!this.getNextUrl()) this.observer.disconnect();
    } catch (error) {
      // If the fetch fails, stop auto-loading and reveal the normal numbered
      // pagination so the visitor can still get to more products by clicking.
      console.error('Infinite scroll: failed to load next page', error);
      this.observer.disconnect();
      if (this.paginationWrapper) this.paginationWrapper.classList.remove('sr-only');
    } finally {
      this.loading = false;
      if (this.status) this.status.hidden = true;
    }
  }
}

customElements.define('infinite-scroll-grid', InfiniteScrollGrid);
